import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { dashboardAPI, userAPI, leaveAPI, attendanceAPI } from "../../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { formatDate, capitalizeFirst, getStatusColor } from "../../lib/utils";
import {
  Users,
  UserCheck,
  Clock,
  CalendarDays,
  AlertTriangle,
  Building2,
  ArrowRight,
  TrendingUp,
  Plus,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const HRAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentLeaves, setRecentLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, leavesRes] = await Promise.all([
        dashboardAPI.getStats(),
        leaveAPI.getAll({ status: "pending" }),
      ]);
      setStats(statsRes.data);
      setRecentLeaves(leavesRes.data.slice(0, 5));
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Employees",
      value: stats?.total_employees || 0,
      icon: Users,
      color: "bg-indigo-100 text-indigo-600",
    },
    {
      title: "Active Employees",
      value: stats?.active_employees || 0,
      icon: UserCheck,
      color: "bg-emerald-100 text-emerald-600",
    },
    {
      title: "Present Today",
      value: stats?.present_today || 0,
      icon: Clock,
      color: "bg-violet-100 text-violet-600",
    },
    {
      title: "Pending Leaves",
      value: stats?.pending_leaves || 0,
      icon: CalendarDays,
      color: "bg-amber-100 text-amber-600",
    },
    {
      title: "Pending Resignations",
      value: stats?.pending_resignations || 0,
      icon: AlertTriangle,
      color: "bg-red-100 text-red-600",
    },
    {
      title: "Departments",
      value: stats?.departments || 0,
      icon: Building2,
      color: "bg-slate-100 text-slate-600",
    },
  ];

  // Sample attendance data for chart
  const attendanceData = [
    { day: "Mon", present: 45, absent: 5 },
    { day: "Tue", present: 48, absent: 2 },
    { day: "Wed", present: 42, absent: 8 },
    { day: "Thu", present: 47, absent: 3 },
    { day: "Fri", present: 44, absent: 6 },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="hradmin-dashboard">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            HR Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Manage employees and HR operations
          </p>
        </div>
        <Link to="/users">
          <Button className="btn-primary" data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Employee
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="stat-card card-hover" data-testid={`stat-card-${index}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-slate-500 mb-1">{stat.title}</p>
                  <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts and Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Trend */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Weekly Attendance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={attendanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="day" stroke="#64748B" />
                  <YAxis stroke="#64748B" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="present"
                    stroke="#4F46E5"
                    fill="#4F46E5"
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Pending Leave Requests */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Pending Leave Requests</CardTitle>
            <Link to="/leaves">
              <Button variant="ghost" size="sm">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentLeaves.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No pending requests</p>
              ) : (
                recentLeaves.map((leave) => (
                  <div
                    key={leave.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-slate-50"
                  >
                    <div>
                      <p className="font-medium text-slate-900">
                        {capitalizeFirst(leave.leave_type)} Leave
                      </p>
                      <p className="text-sm text-slate-500">
                        {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`badge badge-${getStatusColor(leave.status)}`}>
                        {leave.days} days
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/users">
              <Button variant="outline" className="w-full h-20 flex-col gap-2">
                <Users className="w-5 h-5" />
                <span>Manage Users</span>
              </Button>
            </Link>
            <Link to="/leaves">
              <Button variant="outline" className="w-full h-20 flex-col gap-2">
                <CalendarDays className="w-5 h-5" />
                <span>Manage Leaves</span>
              </Button>
            </Link>
            <Link to="/attendance/reports">
              <Button variant="outline" className="w-full h-20 flex-col gap-2">
                <Clock className="w-5 h-5" />
                <span>Attendance Reports</span>
              </Button>
            </Link>
            <Link to="/salary/structures">
              <Button variant="outline" className="w-full h-20 flex-col gap-2">
                <TrendingUp className="w-5 h-5" />
                <span>Salary Structures</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HRAdminDashboard;
